import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../App";
import { toast } from "sonner";
import { X, Search, Trash2, ArrowRightLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransferenciaStockModal({ onClose, onDone }) {
  const { token, activeEmpresaPropia } = useContext(AuthContext);
  const origen = activeEmpresaPropia?.slug || "";
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [destinos, setDestinos] = useState([]);
  const [destino, setDestino] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [notas, setNotas] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [verHistorial, setVerHistorial] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!origen) return;
    fetch(`${API}/admin/transferencias-stock/destinos?logo_tipo=${origen}`, { headers })
      .then(r => (r.ok ? r.json() : [])).then(d => { setDestinos(d); if (d.length === 1) setDestino(d[0].slug); });
    // eslint-disable-next-line
  }, [origen]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!busqueda.trim()) { setResultados([]); return; }
    timer.current = setTimeout(async () => {
      const q = new URLSearchParams({ logo_tipo: origen, page: "1", page_size: "15", con_stock: "true" });
      busqueda.split(/\s+/).filter(Boolean).forEach(t => q.append("terminos", t));
      const r = await fetch(`${API}/admin/productos?${q}`, { headers });
      if (r.ok) setResultados((await r.json()).productos || []);
    }, 250);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [busqueda]);

  const agregar = (p) => {
    if (lineas.some(l => l.producto.id === p.id)) { toast.info("Ya está en la lista"); return; }
    const vars = p.variantes_habilitadas ? (p.variantes || []).filter(v => v.activo !== false) : [];
    setLineas(prev => [...prev, { producto: p, variante_id: vars.length === 1 ? vars[0].id : "", cantidad: "" }]);
    setBusqueda(""); setResultados([]);
  };
  const upd = (id, patch) => setLineas(prev => prev.map(l => (l.producto.id === id ? { ...l, ...patch } : l)));
  const disponible = (l) => {
    const base = l.producto.variantes_habilitadas ? (l.producto.variantes || []).find(v => v.id === l.variante_id) : l.producto;
    return base ? Number(base.stock_actual || 0) - Number(base.stock_reservado || 0) : null;
  };

  const confirmar = async () => {
    if (!destino) return toast.error("Elegí la empresa destino");
    if (!lineas.length) return toast.error("Agregá productos");
    for (const l of lineas) {
      const q = parseFloat(String(l.cantidad).replace(",", "."));
      if (!(q > 0)) return toast.error(`Cantidad inválida en ${l.producto.nombre}`);
      if (l.producto.variantes_habilitadas && !l.variante_id) return toast.error(`Elegí la variante de ${l.producto.nombre}`);
    }
    if (!window.confirm(`Transferir ${lineas.length} producto(s) a ${destinos.find(d => d.slug === destino)?.nombre}?`)) return;
    setGuardando(true);
    try {
      const r = await fetch(`${API}/admin/transferencias-stock`, {
        method: "POST", headers,
        body: JSON.stringify({
          origen_logo: origen, destino_logo: destino, notas,
          items: lineas.map(l => ({ producto_id: l.producto.id, variante_id: l.variante_id || null, cantidad: parseFloat(String(l.cantidad).replace(",", ".")) })),
        }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Error al transferir"); return; }
      const nuevos = d.items.filter(i => i.producto_creado_en_destino).length;
      toast.success(`Transferencia ${d.numero} registrada${nuevos ? ` (${nuevos} producto(s) creado(s) en destino)` : ""}`);
      onDone && onDone();
      onClose();
    } catch { toast.error("Error de conexión"); } finally { setGuardando(false); }
  };

  const cargarHistorial = async () => {
    setVerHistorial(v => !v);
    const r = await fetch(`${API}/admin/transferencias-stock?logo_tipo=${origen}`, { headers });
    if (r.ok) setHistorial(await r.json());
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-3 overflow-y-auto">
      <div className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-3xl my-4">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-white font-bold flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-cyan-400" /> Transferir mercadería</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          {destinos.length === 0 ? (
            <p className="text-sm text-amber-300">Esta empresa no pertenece a un grupo de sucursales. Creá el grupo en Empresas propias → Sucursales.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Origen</label>
                  <div className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white">{activeEmpresaPropia?.nombre || origen}</div>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Destino *</label>
                  <select value={destino} onChange={e => setDestino(e.target.value)} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm">
                    <option value="">Seleccionar empresa...</option>
                    {destinos.map(d => <option key={d.slug} value={d.slug}>{d.nombre}</option>)}
                  </select>
                </div>
              </div>

              <div className="relative">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
                <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar producto por nombre, SKU o código de barras..."
                  className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg pl-9 pr-3 py-2 text-sm" />
                {resultados.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-arandu-dark border border-white/10 rounded-lg max-h-60 overflow-y-auto">
                    {resultados.map(p => (
                      <button key={p.id} onClick={() => agregar(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex justify-between gap-2">
                        <span className="text-white">{p.nombre} <span className="text-slate-500 text-xs">{p.sku}</span></span>
                        <span className="text-slate-400 text-xs">stock {p.stock_actual}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {lineas.length > 0 && (
                <div className="space-y-2">
                  {lineas.map(l => {
                    const disp = disponible(l);
                    return (
                      <div key={l.producto.id} className="bg-arandu-dark border border-white/10 rounded-lg p-3 flex flex-wrap items-center gap-2">
                        <div className="flex-1 min-w-[160px]">
                          <p className="text-white text-sm">{l.producto.nombre}</p>
                          <p className="text-slate-500 text-xs">{l.producto.sku}{disp !== null ? ` · disponible ${disp}` : ""}</p>
                        </div>
                        {l.producto.variantes_habilitadas && (
                          <select value={l.variante_id} onChange={e => upd(l.producto.id, { variante_id: e.target.value })} className="bg-arandu-dark-light border border-white/10 text-white rounded-lg px-2 py-1.5 text-sm">
                            <option value="">Variante...</option>
                            {(l.producto.variantes || []).filter(v => v.activo !== false).map(v => (
                              <option key={v.id} value={v.id}>{v.atributos_texto} (stock {v.stock_actual})</option>
                            ))}
                          </select>
                        )}
                        <input value={l.cantidad} onChange={e => upd(l.producto.id, { cantidad: e.target.value })} placeholder="Cant."
                          inputMode="decimal" className="w-24 bg-arandu-dark-light border border-white/10 text-white rounded-lg px-2 py-1.5 text-sm text-right" />
                        <button onClick={() => setLineas(prev => prev.filter(x => x.producto.id !== l.producto.id))} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>
              )}

              <input value={notas} onChange={e => setNotas(e.target.value)} placeholder="Notas (opcional)"
                className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" />
              <p className="text-xs text-slate-500">Mueve stock a costo, sin factura ni IVA. Si el producto no existe en la empresa destino, se crea automáticamente.</p>
              <div className="flex justify-between items-center">
                <button onClick={cargarHistorial} className="text-xs text-cyan-400 hover:underline">{verHistorial ? "Ocultar historial" : "Ver historial"}</button>
                <button onClick={confirmar} disabled={guardando} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50">
                  {guardando ? "Transfiriendo..." : "Confirmar transferencia"}
                </button>
              </div>
              {verHistorial && (
                <div className="border-t border-white/10 pt-3 space-y-1 max-h-60 overflow-y-auto">
                  {historial.length === 0 && <p className="text-slate-500 text-xs">Sin transferencias.</p>}
                  {historial.map(t => (
                    <div key={t.id} className="text-xs text-slate-300 flex justify-between gap-2">
                      <span><b className="text-white">{t.numero}</b> · {t.fecha} · {t.origen_nombre} → {t.destino_nombre}</span>
                      <span className="text-slate-500">{t.items.length} ítem(s) · {t.usuario_nombre}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
